/**
 * settings/api-urls.ts
 * desc: REST endpoint templates for the tier0-core and Keycloak admin APIs
 * note: `${param}` placeholders are filled with interpolate(); `as const` keeps every value literal, so TemplateData<typeof apiUrls.X> knows the required keys
 */
export default {
    CLIENT_BUCKET_FILE: '/api/v1/client-bucket-files/${filePath}',
    CLIENT_BUCKET_FILES: '/api/v1/client-bucket-files',
    COLLECTION: '/api/v1/collections/${collectionId}',
    COLLECTION_ALIASES: '/api/v1/collections/${collectionId}/search-aliases',
    COLLECTION_ALIAS: '/api/v1/collections/${collectionId}/search-aliases/${aliasId}',
    COLLECTION_ALIAS_REVIEW: '/api/v1/collections/${collectionId}/search-aliases/${aliasId}/review',
    COLLECTION_ALIASES_BATCH_DELETE: '/api/v1/collections/${collectionId}/search-aliases/batch-delete',
    COLLECTION_ALIASES_BATCH_REVIEW: '/api/v1/collections/${collectionId}/search-aliases/batch-review',
    COLLECTION_EXPORT: '/api/v1/collections/${collectionId}/export',
    COLLECTION_ITEM: '/api/v1/collections/${collectionId}/items/${collectionItemId}',
    COLLECTION_ITEMS: '/api/v1/collections/${collectionId}/items',
    COLLECTION_METADATA: '/api/v1/collections/${collectionId}/metadata',
    COLLECTION_METADATA_ITEM: '/api/v1/collections/${collectionId}/metadata/${metadataId}',
    COLLECTION_USAGE: '/api/v1/collections/${collectionId}/usage',
    COLLECTION_UPLOADED_FILES: '/api/v1/collections/${collectionId}/uploaded-files',
    COLLECTIONS: '/api/v1/collections',
    CONSTANT: '/api/v1/constants/${constantId}',
    CONSTANT_USAGE: '/api/v1/constants/${constantId}/usage',
    CONSTANTS: '/api/v1/constants',
    DASHBOARD_DATA: '/api/v1/dashboard/data',
    DASHBOARD_DATA_AVAILABILITY: '/api/v1/dashboard/data-availability',
    ENVIRONMENT: '/api/v1/environments/${environmentId}',
    ENVIRONMENT_USAGE: '/api/v1/environments/${environmentId}/usage',
    ENVIRONMENTS: '/api/v1/environments',
    ENVIRONMENT_FIELD_DEFINITIONS: '/api/v1/environment-field-definitions',
    ENVIRONMENT_FIELD_DEFINITION: '/api/v1/environment-field-definitions/${environmentFieldDefinitionId}',
    ENVIRONMENT_FIELD_DEFINITIONS_BATCH_UPDATE: '/api/v1/environment-field-definitions/batch-update',
    INTENT: '/api/v1/intents/${intentId}',
    INTENT_USAGE: '/api/v1/intents/${intentId}/usage',
    INTENTS: '/api/v1/intents',
    KEYCLOAK_IDENTITY_PROVIDERS: '/admin/realms/${realm}/identity-provider/instances',
    KEYCLOAK_ROLES: '/admin/realms/${realm}/users/${userId}/role-mappings/realm',
    KEYCLOAK_ROLES_AVAILABLE: '/admin/realms/${realm}/users/${userId}/role-mappings/realm/available',
    KEYCLOAK_USER: '/admin/realms/${realm}/users/${userId}',
    KEYCLOAK_USER_EXECUTE_ACTIONS: '/admin/realms/${realm}/users/${userId}/execute-actions-email',
    KEYCLOAK_USER_RESET_PASSWORD: '/admin/realms/${realm}/users/${userId}/reset-password',
    KEYCLOAK_USER_ROLE_MAPPINGS: '/admin/realms/${realm}/users/${userId}/role-mappings/realm',
    KEYCLOAK_USER_ROLE_MAPPINGS_COMPOSITE: '/admin/realms/${realm}/users/${userId}/role-mappings/realm/composite',
    KEYCLOAK_USERS: '/admin/realms/${realm}/users',
    MODEL: '/api/v1/models/${modelId}',
    MODEL_ACCURACY: '/api/v1/models/${modelId}/calculate-accuracy',
    MODEL_PERFORMANCE: '/api/v1/models/${modelId}/performance',
    MODEL_TEST: '/api/v1/models/${modelId}/test',
    MODEL_TRAINING_SET: '/api/v1/models/${modelId}/calculate-training-set-size',
    MODELS: '/api/v1/models',
    ORDER: '/api/v1/orders/${orderId}',
    ORDER_INSIGHT: '/api/v1/orders/${orderId}/insights/${insightId}',
    ORDER_INSIGHTS: '/api/v1/orders/${orderId}/insights',
    ORDER_VIOLATIONS: '/api/v1/orders/${orderId}/violations',
    ORDERS: '/api/v1/orders',
    ORDER_FILES: '/api/v1/orders/${orderId}/files',
    ORDER_FILE: '/api/v1/orders/${orderId}/files/${fileId}',
    ORDER_SCHEMAS: '/api/v1/order-schemas',
    ORDER_SCHEMAS_ITEM: '/api/v1/order-schemas/${orderSchemaId}',
    ORDER_SCHEMA_USAGE: '/api/v1/order-schemas/${orderSchemaId}/usage',
    ORDER_SCHEMA_AUTOCOMPLETE: '/api/v1/order-schemas/${orderSchemaId}/tables/${metadataTableName}/columns/${columnName}',
    PATH: '/api/v2/paths/${pathId}',
    PATH_COPY: '/api/v2/paths/${pathType}/${pathId}/copy',
    PATH_DEBUG_SESSIONS: '/api/v1/path-debug-sessions',
    PATH_DEBUG_SESSION: '/api/v1/path-debug-sessions/${sessionId}',
    PATH_DEBUG_SESSION_CONTINUE: '/api/v1/path-debug-sessions/${sessionId}/continue',
    PATH_DEBUG_SESSION_MOCKS: '/api/v1/path-debug-sessions/${sessionId}/mocks',
    PATH_DEBUG_SESSION_MOCK: '/api/v1/path-debug-sessions/${sessionId}/mocks/${mockId}',
    PATH_DEBUG_SESSION_PAUSE: '/api/v1/path-debug-sessions/${sessionId}/pause',
    PATH_DEBUG_SESSION_RUNS: '/api/v1/path-debug-sessions/${sessionId}/runs',
    PATH_DEBUG_SESSION_RUN_STEPS: '/api/v1/path-debug-sessions/${sessionId}/runs/${runId}/steps',
    PATH_DEBUG_SESSION_STEP_RUNS: '/api/v1/path-debug-sessions/${sessionId}/steps/${stepId}/runs',
    PATH_DEBUG_SESSION_STOP: '/api/v1/path-debug-sessions/${sessionId}/stop',
    PATH_EXPORT: '/api/v2/paths/${pathId}/export',
    PATH_PROMOTE: '/api/v2/paths/current/${pathId}/promote',
    PATH_PUBLISH: '/api/v2/paths/draft/${pathId}/publish',
    PATH_USAGE: '/api/v2/paths/${pathId}/usage',
    PATH_STEP: '/api/v2/paths/${pathType}/${pathId}/steps/${stepId}',
    PATH_STEP_UPDATE: '/api/v2/paths/draft/${pathId}/steps/${stepId}/update',
    PATH_STEPS: '/api/v2/paths/${pathType}/${pathId}/steps',
    PATH_VERSIONS: '/api/v2/paths/${pathId}/versions',
    PATH_VERSION_COPY: '/api/v2/paths/${pathId}/versions/${versionId}/copy',
    PATH_VERSION_REVERT: '/api/v2/paths/${pathId}/versions/${versionId}/revert',
    PATH_VERSION_STEPS: '/api/v2/paths/versions/${versionId}/steps',
    PATH_VERSION_VARIABLES: '/api/v2/paths/versions/${versionId}/variable-definitions',
    PATH_TYPE: '/api/v2/paths/${pathType}/${pathId}',
    PATH_UPDATE_SOURCE: '/api/v2/paths/${pathType}/${pathId}/update-source',
    PATH_UPDATE_OUTPUT_REFERENCE: '/api/v2/paths/${pathType}/${pathId}/update-step-output-references',
    PATHS: '/api/v2/paths',
    PATHS_IMPORT: '/api/v2/paths/import',
    PATHS_TYPE: '/api/v2/paths/${pathType}',
    PATH_VARIABLE: '/api/v2/paths/${pathType}/${pathId}/variable-definitions/${variableId}',
    PATH_VARIABLE_USAGE: '/api/v2/paths/${pathType}/${pathId}/variable-definitions/${variableId}/usage',
    PATH_VARIABLES: '/api/v2/paths/${pathType}/${pathId}/variable-definitions',
    REDACTIONS_GDPR_DELETION: '/api/v1/redaction/gdpr-deletion',
    REPORTS_TRAINING_DATA: '/api/v1/reports/training-data-per-model',
    REVIEW_QUEUE: '/api/v1/review-queues/${reviewQueueId}',
    REVIEW_QUEUE_USAGE: '/api/v1/review-queues/${reviewQueueId}/usage',
    REVIEW_QUEUE_ITEM: '/api/v1/review-queues/${reviewQueueId}/items/${reviewItemId}',
    REVIEW_QUEUE_ITEM_ASSIGN: '/api/v1/review-queues/${reviewQueueId}/items/${reviewItemId}/assign',
    REVIEW_QUEUE_ITEM_NEXT: '/api/v1/review-queues/${reviewQueueId}/items/next',
    REVIEW_QUEUES: '/api/v1/review-queues',
    RULE: '/api/v1/rules/${ruleId}',
    RULES: '/api/v1/rules',
    RULE_SET: '/api/v1/rule-sets/${ruleSetId}',
    RULE_SETS: '/api/v1/rule-sets',
    RULE_SET_EVALUATIONS: '/api/v1/rule-set-evaluations',
    RULE_SET_EVALUATION: '/api/v1/rule-set-evaluations/${ruleSetEvaluationId}',
    SOURCE: '/api/v2/sources/${sourceId}',
    SOURCE_DIRECTORY: '/api/v1/sources/${sourceId}/directories/${directoryId}',
    SOURCE_DIRECTORIES: '/api/v1/sources/${sourceId}/directories',
    SOURCE_MESSAGE: '/api/v1/sources/${sourceId}/messages/${messageId}',
    SOURCE_MESSAGE_ATTACHMENT: '/api/v1/sources/${sourceId}/messages/${messageId}/attachments/${attachmentId}',
    SOURCE_MESSAGE_INTERNAL: '/api/v1/messages/${messageId}',
    SOURCE_MESSAGES: '/api/v1/sources/${sourceId}/directories/${directoryId}/messages',
    SOURCE_USAGE: '/api/v2/sources/${sourceId}/usage',
    SOURCES: '/api/v2/sources',
    SOURCES_UPLOAD_FILE: '/api/v1/sources/${sourceId}/uploaded-files/${uploadFileId}',
    SOURCES_UPLOAD_FILES: '/api/v1/sources/${sourceId}/uploaded-files',
    SOURCE_TYPE: '/api/v1/source-types/${sourceTypeId}',
    SOURCE_TYPE_USAGE: '/api/v1/source-types/${sourceTypeId}/usage',
    SOURCE_TYPES: '/api/v1/source-types',
    STEP_FACTORY: '/api/v1/step-factories/${stepFactoryId}',
    STEP_FACTORY_USAGE: '/api/v1/step-factories/${stepFactoryId}/usage',
    STEP_FACTORIES: '/api/v1/step-factories',
    STEP_FACTORY_VERSION: '/api/v1/step-factories/${stepFactoryId}/versions/${stepFactoryVersionId}',
    STEP_FACTORY_VERSION_AUTOCOMPLETE: '/api/v1/step-factories/autocomplete/image/${dockerImageName}/tag/${dockerImageTag}',
    STEP_FACTORY_VERSION_USAGE: '/api/v1/step-factories/${stepFactoryId}/versions/${stepFactoryVersionId}/usage',
    STEP_FACTORY_VERSIONS: '/api/v1/step-factories/${stepFactoryId}/versions',
    TEST_SESSION_FILES: '/api/v1/test-sessions/${testSessionId}/files',
    TEST_SESSION_FILE: '/api/v1/test-sessions/${testSessionId}/files/${filePath}',
    TRAINING_ENGINE: '/api/v1/training-engines/${trainingEngineId}',
    TRAINING_ENGINES: '/api/v1/training-engines',
    TRIP: '/api/v1/trips/${tripId}',
    TRIP_RESTART: '/api/v1/trips/${tripId}/restart',
    TRIP_RESTART_BULK: '/api/v1/trips/bulk-restart',
    TRIP_STOP: '/api/v1/trips/${tripId}/stop',
    TRIP_STOP_BULK: '/api/v1/trips/bulk-stop',
    TRIP_MESSAGE: '/api/v1/trips/${tripId}/message',
    TRIPS: '/api/v1/trips',
    USER: '/api/v1/users/${userId}',
    USER_ME: '/api/v1/users/me',
    USERS: '/api/v1/users',
    WEBHOOK_SOURCE: '/api/v1/webhooks/${sourceId}',
    WIKI_PAGE: '/api/v1/wiki-pages/${wikiPageId}',
    WIKI_PAGES: '/api/v1/wiki-pages',
    WORKFLOWS_VALIDATE_EXPRESSION: '/api/v1/workflows/expression/validate'
} as const;
